// src/pages/admin/Page_Leads/CampaignsPanel.js
//
// Campanas de promociones por lotes: seguimiento y control.
//
// Lo que se ve aqui es el estado de un envio masivo en curso: cuanto lleva,
// cuanto queda, y sobre todo el SEMAFORO DE ENVIO. Ese semaforo no es adorno:
// la misma cuenta SES manda las confirmaciones de compra, asi que si los
// rebotes suben la campana se para sola y aqui se explica por que.
//
// La creacion de campanas NO se hace desde aqui: la lista se prepara y se
// depura fuera, y se carga por API. Este panel es para vigilar y frenar.

import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Tag,
  Button,
  Progress,
  Alert,
  Space,
  Statistic,
  Row,
  Col,
  Tooltip,
  Popconfirm,
  Typography,
  InputNumber,
  message,
} from "antd";
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  ThunderboltOutlined,
  ReloadOutlined,
  StopOutlined,
  UnorderedListOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useAuth } from "../../../hooks/useAuth";
import { promotionsApi } from "../../../api/promotionsApi";
import { CampaignRecipientsDrawer } from "./CampaignRecipientsDrawer";

const { Text } = Typography;

const ESTADO = {
  draft: { color: "default", label: "Borrador" },
  running: { color: "processing", label: "En marcha" },
  paused: { color: "warning", label: "Pausada" },
  completed: { color: "success", label: "Terminada" },
  aborted: { color: "error", label: "Cancelada" },
};

// Espejo de MAX_AUTO_RESUMES en server/services/cron/promoCampaignCron.js.
// Solo sirve para saber si toca decir "se reanuda sola" o "mira la lista": el
// que decide de verdad es el cron.
const MAX_AUTO_RESUMES = 3;

export function CampaignsPanel() {
  const { accessToken } = useAuth();
  const [items, setItems] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(null);
  const [loteManual, setLoteManual] = useState(25);
  // Campana cuyo listado de destinatarios se esta mirando.
  const [verDestinatarios, setVerDestinatarios] = useState(null);

  const load = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const [camp, salud] = await Promise.all([
        promotionsApi.listCampaigns(accessToken),
        promotionsApi.sesHealth(accessToken).catch(() => null),
      ]);
      setItems(camp?.data?.items || []);
      setHealth(salud?.data || null);
    } catch (err) {
      message.error(`Error cargando campanas: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  const accion = useCallback(
    async (id, action, body, textoOk) => {
      setActing(`${id}:${action}`);
      try {
        const res = await promotionsApi.campaignAction(
          accessToken,
          id,
          action,
          body,
        );
        const r = res?.data?.resumen;
        if (r?.parada) {
          message.warning(`Lote detenido: ${r.parada}`);
        } else if (r && typeof r.revisados === "number") {
          // Depuracion de lista: no envia, solo saca del censo lo que iba a
          // rebotar.
          message.success(
            `Lista depurada: ${r.descartados} descartados de ${r.revisados} revisados. Quedan ${r.validos} validos.`,
          );
        } else if (r) {
          message.success(
            `Lote: ${r.enviados} enviados, ${r.saltados} saltados` +
              (r.descartados ? ` (${r.descartados} dominios muertos)` : "") +
              `, ${r.fallidos} fallidos. Quedan ${r.restantes}.`,
          );
        } else {
          message.success(textoOk || "Hecho");
        }
        load();
      } catch (err) {
        message.error(err.message || "Error");
      } finally {
        setActing(null);
      }
    },
    [accessToken, load],
  );

  const columns = [
    {
      title: "Campana",
      key: "name",
      render: (_, row) => (
        <div>
          <div style={{ fontWeight: 600 }}>{row.name}</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>
            -{row.discount_pct}% · {row.duration_days} dias · lotes de{" "}
            {row.batch_size}
          </div>
        </div>
      ),
    },
    {
      title: "Estado",
      dataIndex: "status",
      width: 120,
      render: (v, row) => {
        const e = ESTADO[v] || { color: "default", label: v };
        // Una pausa automatica no obliga a hacer nada: el tick de las 09:00 la
        // reanuda cuando la salud vuelva. Decirlo aqui evita el reflejo de
        // entrar a darle a "Reanudar" a mano todos los dias.
        const frenoAuto = v === "paused" && row.paused_by === "auto";
        const agotada = (row.auto_pause_count || 0) > MAX_AUTO_RESUMES;
        return (
          <div>
            <Tooltip title={row.paused_reason || ""}>
              <Tag color={e.color}>{e.label}</Tag>
            </Tooltip>
            {frenoAuto && (
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                {agotada
                  ? "Frenada varias veces: revisa la lista"
                  : "Se reanuda sola al recuperarse"}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Avance",
      key: "progress",
      width: 260,
      render: (_, row) => {
        const p = row.progress || {};
        return (
          <div>
            <Progress
              percent={p.pct || 0}
              size="small"
              status={row.status === "paused" ? "exception" : undefined}
            />
            <div style={{ fontSize: 12, color: "#475569" }}>
              {p.sent || 0} enviados · {p.pending || 0} pendientes
              {p.skipped ? ` · ${p.skipped} saltados` : ""}
              {p.failed ? ` · ${p.failed} fallidos` : ""}
            </div>
          </div>
        );
      },
    },
    {
      title: "Ultimo lote",
      dataIndex: "last_batch_at",
      width: 130,
      render: (v) =>
        v ? (
          dayjs(v).format("DD/MM HH:mm")
        ) : (
          <Text type="secondary">sin empezar</Text>
        ),
    },
    {
      title: "Acciones",
      key: "acciones",
      width: 300,
      render: (_, row) => {
        const enMarcha = row.status === "running";
        const terminada =
          row.status === "completed" || row.status === "aborted";
        return (
          <Space wrap>
            <Button
              size="small"
              icon={<UnorderedListOutlined />}
              onClick={() => setVerDestinatarios(row)}
            >
              Ver lista
            </Button>
            {!terminada && (
              <Popconfirm
                title="Depurar la lista"
                description="Comprueba el dominio de cada pendiente y saca del censo los que no admiten correo. No envia nada."
                okText="Depurar"
                cancelText="No"
                onConfirm={() => accion(row._id, "verify")}
              >
                <Tooltip title="Quita los dominios muertos antes de gastar envios en ellos">
                  <Button
                    size="small"
                    icon={<SafetyCertificateOutlined />}
                    loading={acting === `${row._id}:verify`}
                  >
                    Depurar lista
                  </Button>
                </Tooltip>
              </Popconfirm>
            )}
            {!enMarcha && !terminada && (
              <Button
                size="small"
                type="primary"
                icon={<PlayCircleOutlined />}
                loading={acting === `${row._id}:start`}
                onClick={() =>
                  accion(row._id, "start", null, "Campana en marcha")
                }
              >
                {row.status === "paused" ? "Reanudar" : "Arrancar"}
              </Button>
            )}
            {enMarcha && (
              <Button
                size="small"
                icon={<PauseCircleOutlined />}
                loading={acting === `${row._id}:pause`}
                onClick={() =>
                  accion(row._id, "pause", null, "Campana pausada")
                }
              >
                Pausar
              </Button>
            )}
            {enMarcha && (
              <Popconfirm
                title={`Enviar ${loteManual} correos ahora`}
                description="Se creara una promocion y se enviara el correo a cada uno. No se puede deshacer."
                okText="Enviar lote"
                cancelText="No"
                onConfirm={() => accion(row._id, "run", { size: loteManual })}
              >
                <Button
                  size="small"
                  icon={<ThunderboltOutlined />}
                  loading={acting === `${row._id}:run`}
                  disabled={health && !health.healthy}
                >
                  Lote ahora
                </Button>
              </Popconfirm>
            )}
            {!terminada && (
              <Popconfirm
                title="Cancelar la campana"
                description="No se podra reanudar. Las promos ya enviadas siguen validas."
                okText="Cancelar campana"
                cancelText="Volver"
                onConfirm={() =>
                  accion(row._id, "abort", null, "Campana cancelada")
                }
              >
                <Button size="small" danger icon={<StopOutlined />} />
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ maxWidth: 1200 }}>
      {/* Semaforo de envio */}
      {health && (
        <Alert
          type={
            !health.available ? "error" : health.healthy ? "success" : "warning"
          }
          showIcon
          style={{ marginBottom: 16 }}
          message={
            !health.available
              ? "No se puede vigilar el envio"
              : health.healthy
                ? "Envio en condiciones"
                : "Envio bloqueado"
          }
          description={
            !health.available ? (
              health.reason
            ) : (
              <Row gutter={16} style={{ marginTop: 4 }}>
                <Col xs={12} sm={6}>
                  <Statistic
                    title="Margen diario"
                    value={health.quota?.remaining ?? 0}
                    suffix={`/ ${health.quota?.max24h ?? 0}`}
                  />
                </Col>
                {/* Con menos de 20 envios en 48h el porcentaje no significa
                    nada: un solo email mal tecleado da un 11% que asusta sin
                    motivo. Mostramos el dato en gris y avisamos, en vez de
                    pintar una alarma roja que contradice al semaforo. */}
                <Col xs={12} sm={6}>
                  <Statistic
                    title="Tasa de rebote"
                    value={health.stats?.bounceRate ?? 0}
                    suffix="%"
                    valueStyle={
                      !health.stats?.significant
                        ? { color: "#94a3b8" }
                        : {
                            color:
                              (health.stats?.bounceRate ?? 0) >= 3
                                ? "#c63f3e"
                                : "#16a34a",
                          }
                    }
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic
                    title="Tasa de quejas"
                    value={health.stats?.complaintRate ?? 0}
                    suffix="%"
                    valueStyle={
                      !health.stats?.significant
                        ? { color: "#94a3b8" }
                        : {
                            color:
                              (health.stats?.complaintRate ?? 0) >= 0.08
                                ? "#c63f3e"
                                : "#16a34a",
                          }
                    }
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic
                    title="Muestra (48h)"
                    value={health.stats?.attempts ?? 0}
                    suffix="envios"
                  />
                </Col>
                {health.stats && !health.stats.significant && (
                  <Col span={24} style={{ marginTop: 8 }}>
                    <Text type="secondary">
                      Muestra pequena ({health.stats.attempts} envios en 48h):
                      los porcentajes de arriba no son concluyentes y no frenan
                      la campana. A partir de 20 envios empiezan a contar.
                    </Text>
                  </Col>
                )}
                {!health.healthy && (
                  <Col span={24} style={{ marginTop: 8 }}>
                    <Text type="danger">{health.blockers?.join(" ")}</Text>
                  </Col>
                )}
              </Row>
            )
          }
        />
      )}

      <Card
        title="Campanas de promociones"
        size="small"
        extra={
          <Space>
            <Tooltip title="Cuantos correos manda el boton 'Lote ahora'">
              <InputNumber
                size="small"
                min={1}
                max={200}
                value={loteManual}
                onChange={(v) => setLoteManual(v || 1)}
                addonBefore="Lote"
              />
            </Tooltip>
            <Button size="small" icon={<ReloadOutlined />} onClick={load}>
              Refrescar
            </Button>
          </Space>
        }
      >
        <Table
          rowKey="_id"
          size="small"
          loading={loading}
          dataSource={items}
          columns={columns}
          pagination={false}
          locale={{
            emptyText:
              "No hay campanas. Se crean cargando la lista depurada por API.",
          }}
        />
        <Text
          type="secondary"
          style={{ display: "block", marginTop: 10, fontSize: 12 }}
        >
          El cron diario de las 09:00 suelta UN lote de cada campana en marcha,
          saltandose los dominios que no admiten correo. Si la tasa de rebote o
          de quejas sube por encima del limite, la campana se pausa sola y aqui
          aparece el motivo — y se reanuda sola en cuanto la salud vuelve, sin
          que tengas que entrar. Solo hace falta que la mires si se ha frenado
          varias veces seguidas: eso ya no es un pico, es la lista.
        </Text>
      </Card>

      <CampaignRecipientsDrawer
        campaign={verDestinatarios}
        open={!!verDestinatarios}
        onClose={() => setVerDestinatarios(null)}
      />
    </div>
  );
}
